import ShopWindowConfig from "@/plugin/ShopWindowConfig";
import { eventNames, valueNames } from "@/modules/Constants";
import * as shareUtils from "@/modules/shareUtils";
import { shareTypes } from "@/modules/shareUtils";
import appController from "@/modules/appController";
import config from "@/modules/config";
import { orientationType } from "@/modules/orientationManager";
import * as orientationLandscape from "@/modules/orientationLandscape";
import * as orientationPortrait from "@/modules/orientationPortrait";
import { updateTemplate } from "@/stores/experience/actions/layout";
import EventDispatcher from "@/common/EventDispatcher";
import type { Orientation } from "@/modules/orientationManager";
import type { ExperienceState, PluginEvent } from "@/types/state";

export const shopWindowNames = {};

/**
 * The fields events carry, by event. Each event sets only its own fields; they
 * are typed as required because each handler reads only what its event sets.
 */
interface AppEvent extends PluginEvent {
  region: string;
  configName: string;
  index: number;
  clusterName: string;
  cindex: number;
  items: { id: unknown; checked: boolean }[];
  value: number;
  timelineName: string;
  marker: unknown;
  shopWindowConfig: ShopWindowConfig | null | undefined;
  time: number;
  duration: number;
  idexperience: unknown;
  linkType: string;
  link: string;
}

/*
 * The root plugin is at the root of the plugin stack.
 */
export default class RootPlugin {
  static SHOP_WINDOW_TEMPLATES: ShopWindowConfig[] = [];

  declare _dispatcher: EventDispatcher<AppEvent>;
  declare shopWindowMap: Record<string, ShopWindowConfig>;

  constructor() {
    const dispatcher = new EventDispatcher<AppEvent>();

    dispatcher.on(eventNames.DECREMENT_SHOP_WINDOW_INDEX, e => {
      appController.decrementShopWindowIndex(e.region, e.configName);
      return true;
    });
    dispatcher.on(eventNames.INCREMENT_SHOP_WINDOW_INDEX, e => {
      appController.incrementShopWindowIndex(e.region, e.configName);
      return true;
    });
    dispatcher.on(eventNames.SHOP_WINDOW_INDEX_CHANGED, e => {
      appController.setShopWindowIndex(e.region, e.configName, e.index);
      return true;
    });
    dispatcher.on(eventNames.USER_INTERACTION_START, e => {
      appController.onUserInteractionStart(e.region);
      return true;
    });
    dispatcher.on(eventNames.USER_INTERACTION_END, e => {
      appController.onUserInteractionEnd(e.region);
      return true;
    });
    dispatcher.on(eventNames.ARTICLE_TRANSITION_START, e => {
      appController.onArticleTransitionStart(e.region, e.configName, e.index);
      return true;
    });
    dispatcher.on(eventNames.ARTICLE_TRANSITION_END, e => {
      appController.onArticleTransitionEnd(e.region, e.configName, e.index);
      return true;
    });
    dispatcher.on(eventNames.ADD_FAVORITE, e => {
      appController.addFavorite(e.region, e.clusterName, e.cindex, e.index);
      return true;
    });
    dispatcher.on(eventNames.UPDATE_FAVORITES, e => {
      appController.updateFavorites(e.region, e.items);
      return true;
    });
    dispatcher.on(eventNames.SHOW_FAVORITES, e => {
      appController.showFavorites(e.region);
      return true;
    });
    dispatcher.on(eventNames.OPEN_SHOP_WINDOW, e => {
      appController.openShopWindow(e.region);
      return true;
    });
    dispatcher.on(eventNames.CLOSE_SHOP_WINDOW, e => {
      appController.closeShopWindow(e.region);
      return true;
    });
    dispatcher.on(eventNames.DISMISS_FAVORITES, e => {
      appController.dismissFavorites(e.region);
      return true;
    });
    dispatcher.on(eventNames.EXIT, e => {
      appController.exit(e.region);
      return true;
    });
    dispatcher.on(eventNames.PLAY, e => {
      appController.play(e.region);
      return true;
    });
    dispatcher.on(eventNames.VIDEO_POSITION, e => {
      appController.onSetVideoPosition(e.region, e.value);
      return true;
    });
    dispatcher.on(eventNames.VIDEO_MARKER_SELECTED, e => {
      appController.onVideoMarkerSelected(e.region, e.timelineName, e.marker);
      return true;
    });
    dispatcher.on(eventNames.VIDEO_MARKER_REACHED, e => {
      appController.onVideoMarkerReached(e.region, e.timelineName, e.marker);
      return true;
    });
    dispatcher.on(eventNames.LINK, e => {
      appController.linkCta(e);
      return true;
    });
    dispatcher.on(eventNames.SET_SHOP_WINDOW, e => {
      appController.setShopWindow(e.region, e.shopWindowConfig);
      return true;
    });
    dispatcher.on(eventNames.SHOP_WINDOW_EXIT, e => {
      appController.onShopWindowExit(e.region, e.shopWindowConfig);
      return true;
    });
    dispatcher.on(eventNames.TIME_SYNC, e => {
      appController.onTimeSync(e.region, e.timelineName, e.time, e.duration);
      return true;
    });
    dispatcher.on(eventNames.VIDEO_ENDED, e => {
      appController.onVideoEnded(e.timelineName);
      return true;
    });
    dispatcher.on(eventNames.ACTION_SHEET_EXIT, e => {
      appController.onActionSheetExit(
        e.region,
        e.clusterName,
        e.cindex,
        e.index,
      );
      return true;
    });
    dispatcher.on(eventNames.CHAIN_ADVANCE, e => {
      appController.advanceToChainExperience(e.idexperience);
      return true;
    });
    dispatcher.on(eventNames.PIXIE_RESUME, e => {
      appController.resumeFromPixie(e.region);
      return true;
    });

    this._dispatcher = dispatcher;
  }

  init(state: ExperienceState) {
    const experienceData = state.experienceData;

    this.shopWindowMap = {};

    for (
      let n = 0, max = RootPlugin.SHOP_WINDOW_TEMPLATES.length;
      n < max;
      n++
    ) {
      const template = RootPlugin.SHOP_WINDOW_TEMPLATES[n];
      const config = ShopWindowConfig.fromTemplate(template);
      this.shopWindowMap[config.name] = config.init(experienceData);
    }

    this._updateOrientationData(
      state,
      state.applicationData.getValue(valueNames.ORIENTATION) as Orientation,
    );
  }

  getShopWindowConfig(name: string): ShopWindowConfig | undefined {
    return this.shopWindowMap[name];
  }

  /*
   * handles an event.
   * returns true if the event was handled which prevents
   * other plugins from handling the event.
   */
  handleEvent(state: ExperienceState, e: AppEvent): boolean {
    let handled = false;

    if (e.name === eventNames.SHARE_LINK) {
      this._onShare(e.region, e.linkType, e.link);
      handled = true;
    } else {
      handled = this._dispatcher.dispatch(e.name, e);
    }

    return handled;
  }

  willSetValue(state: ExperienceState, key: string, value: unknown) {
    if (key === valueNames.ORIENTATION) {
      this._onOrientation(state, value as Orientation);
    } else if (key === valueNames.SHOP_WINDOW_CONFIG) {
      if (value) {
        appController.openShopWindow("RecipePlugin");
        const videoTimelineName = this._getVideoTimelineName(
          state,
          value as ShopWindowConfig,
        );
        if (videoTimelineName) {
          appController.setApplicationValue(
            "RecipePlugin",
            valueNames.VIDEO_TIMELINE_NAME,
            videoTimelineName,
          );
        }
      }
      return true;
    }
  }

  _onShare(region: string, key: string, value: string) {
    if (key === shareTypes.FACEBOOK) {
      shareUtils.shareFacebook(value);
    } else if (key === shareTypes.TWITTER) {
      shareUtils.shareTwitter(value);
    } else if (key === shareTypes.PINTEREST) {
      shareUtils.sharePinterest(value);
    } else if (key === shareTypes.EMAIL) {
      shareUtils.shareEmail(value);
    }
  }

  _updateOrientationData(state: ExperienceState, orientation: Orientation) {
    const applicationData = state.applicationData;
    applicationData.setHasMobileExit(
      orientation === orientationType.PORTRAIT &&
        (config.shownAs() !== null || config.hasMobileExit()),
    );
    applicationData.setHasDesktopExit(
      orientation === orientationType.LANDSCAPE &&
        config.isOverlay() &&
        config.hasDesktopExit(),
    );
    applicationData.setHasFavoriteDetailsDescription(
      orientation === orientationType.LANDSCAPE,
    );
  }

  // A rotation must not carry the other orientation's shop-window open state
  // across the boundary — landscape is video-first and always starts closed;
  // portrait falls back to its own configured default. Mirrors the same
  // orientation-aware rule ApplicationData's constructor uses for the
  // initial value, through the same setShowShopWindow() setter
  // handleOpenShopWindow/handleCloseShopWindow use — no parallel state.
  // Runs before _updateTemplate() below so the updateTemplate dispatch it
  // triggers picks up the reset value via _updateTemplateData().
  _resetShopWindowForOrientation(
    state: ExperienceState,
    orientation: Orientation,
  ) {
    state.applicationData.setShowShopWindow(
      orientation === orientationType.LANDSCAPE
        ? false
        : config.showShopWindow(),
    );
  }

  _updateTemplate(state: ExperienceState, orientation: Orientation) {
    let template;
    if (orientation === orientationType.PORTRAIT) {
      template = orientationPortrait;
    } else {
      template = orientationLandscape;
    }

    updateTemplate(template);
  }

  _onOrientation(state: ExperienceState, value: Orientation) {
    this._updateOrientationData(state, value);
    this._resetShopWindowForOrientation(state, value);
    this._updateTemplate(state, value);
  }

  _getVideoTimelineName(
    state: ExperienceState,
    shopWindowConfig: ShopWindowConfig | null | undefined,
  ): string | undefined {
    const experienceData = state.experienceData;
    let ret: string | undefined;

    if (
      shopWindowConfig &&
      experienceData.getTimelineData(shopWindowConfig.clusterName)
    ) {
      ret = shopWindowConfig.clusterName;
    }

    return ret;
  }
}
